// components/AuthComponents/AuthUI/otp-input.tsx
"use client";

import React, { useState, useRef, useEffect } from "react";

interface OTPInputProps {
  length?: number;
  onComplete?: (code: string) => void;
  className?: string;
  autoFocus?: boolean;
}

export default function StandaloneOTPInput({
  length = 6,
  onComplete,
  className = "",
  autoFocus = true,
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeInput, setActiveInput] = useState(0);

  // Générateur de HTML classes
  const cn = (...classes: (string | boolean | undefined)[]) =>
    classes.filter(Boolean).join(" ");

  // Réduire ou étendre refs quand length change
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Hidden input ref for autofill
  const hiddenRef = useRef<HTMLInputElement>(null);
  
  // Mutation observer to detect Chrome autofill
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!hiddenRef.current) return;

    // Set up mutation observer to detect Chrome autofill
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const value = (mutation.target as HTMLInputElement).value;
          if (value && value.length === length) {
            distribute(value);
          }
        }
      });
    });

    observerRef.current.observe(hiddenRef.current, {
      attributes: true,
      attributeFilter: ['value']
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [length]);

  // autofocus sur le hidden input pour capturer autofill
  useEffect(() => {
    if (autoFocus && hiddenRef.current) {
      hiddenRef.current.focus();
    }
  }, [autoFocus]);

  // Si tout le code est rempli, on détecte et déclenche onComplete
  useEffect(() => {
    const code = otp.join("");
    if (code.length === length && !hasSubmitted) {
      setHasSubmitted(true);
      onComplete?.(code);
    }
    if (code.length < length && hasSubmitted) {
      setHasSubmitted(false);
    }
  }, [otp, length, hasSubmitted, onComplete]);

  // distribue un string de longueur >1 dans otp[]
  const distribute = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, length).split("");
    if (digits.length === 0) return;
    
    const newOtp = [...otp];
    digits.forEach((d, i) => {
      if (i < length) {
        newOtp[i] = d;
      }
    });
    
    setHasSubmitted(false);
    setOtp(newOtp);
    
    // Focus the last filled input
    const lastFilledIdx = Math.min(digits.length - 1, length - 1);
    inputRefs.current[lastFilledIdx]?.focus();
    setActiveInput(lastFilledIdx);
  };

  // Handle Chrome's partial autofill (where it only fills the first input)
  useEffect(() => {
    const checkForPartialAutofill = () => {
      // Check if first input has a value but others don't
      if (otp[0] && !otp.slice(1).some(Boolean)) {
        // Check if the first input's value has multiple digits
        const firstInput = inputRefs.current[0];
        if (firstInput && firstInput.value.length > 1) {
          distribute(firstInput.value);
          // Reset the first input to just the first digit
          firstInput.value = firstInput.value[0];
        }
      }
    };

    const timer = setTimeout(checkForPartialAutofill, 300);
    return () => clearTimeout(timer);
  }, [otp]);

  // gestion de la saisie manuelle / single-digit
  const handleChange = (digit: string, idx: number) => {
    if (!/^\d$/.test(digit)) return;
    const newOtp = [...otp];
    newOtp[idx] = digit;
    setOtp(newOtp);
    if (idx < length - 1) {
      inputRefs.current[idx + 1]?.focus();
      setActiveInput(idx + 1);
    } else {
      inputRefs.current[idx]?.blur();
    }
  };

  // capture de l'événement input (saisie normale ou autofill)
  const handleInput = (
    e: React.FormEvent<HTMLInputElement>,
    idx: number
  ) => {
    const v = e.currentTarget.value;
    if (v.length > 1) {
      distribute(v);
      // Reset the input to just the first digit if it's the first input
      if (idx === 0) {
        e.currentTarget.value = v[0];
      }
    } else {
      handleChange(v, idx);
    }
  };

  // gestion des flèches et backspace
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (newOtp[idx]) {
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
        setActiveInput(idx - 1);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
      setActiveInput(idx - 1);
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      inputRefs.current[idx + 1]?.focus();
      setActiveInput(idx + 1);
    }
  };

  // paste (desktop)
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    distribute(e.clipboardData.getData("text/plain"));
  };

  // focus styling
  const handleFocus = (idx: number) => {
    setActiveInput(idx);
  };

  return (
    <div className={cn("relative", className)}>
      {/** 🔒 Hidden input pour capter l'autofill iOS/Chrome */}
      <input
        ref={hiddenRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern={`\\d{${length}}`}
        value={otp.join("")}
        onChange={(e) => distribute(e.currentTarget.value)}
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
      />

      {/** 🎯 Vos 6 cases visibles */}
      <div className="flex items-center justify-center gap-2">
        {/* Première moitié */}
        <div className="flex gap-2">
          {Array.from({ length: Math.min(3, length) }).map((_, i) => (
            <div key={i} className="relative">
              <input
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                value={otp[i] || ""}
                onInput={(e) => handleInput(e as any, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                onFocus={() => handleFocus(i)}
                className={cn(
                  "w-12 h-14 text-center text-xl font-semibold rounded-xl border border-border bg-background text-foreground transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                  activeInput === i && "border-primary ring-2 ring-primary/20",
                  otp[i] && "scale-[1.02]"
                )}
                aria-label={`Digit ${i + 1}`}
              />
            </div>
          ))}
        </div>

        <div className="text-2xl font-bold text-muted-foreground self-center pb-2">
          ·
        </div>

        {/* Deuxième moitié */}
        <div className="flex gap-2">
          {Array.from({ length: Math.max(0, length - 3) }).map((_, j) => {
            const i = j + 3;
            return (
              <div key={i} className="relative">
                <input
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  value={otp[i] || ""}
                  onInput={(e) => handleInput(e as any, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onPaste={handlePaste}
                  onFocus={() => handleFocus(i)}
                  className={cn(
                    "w-12 h-14 text-center text-xl font-semibold rounded-xl border border-border bg-background text-foreground transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    activeInput === i && "border-primary ring-2 ring-primary/20",
                    otp[i] && "scale-[1.02]"
                  )}
                  aria-label={`Digit ${i + 1}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}